import React, { useState, useEffect, useMemo } from 'react';
import { FiCalendar, FiLayers } from 'react-icons/fi';

const TimeRangeSelector = ({
  value,
  onChange,
  comparisonMode = false,
  onComparisonModeChange,
  comparisonValue,
  onComparisonChange,
  comparisonType = 'custom',
  onComparisonTypeChange,
  showComparison = true
}) => {
  const [showCustom, setShowCustom] = useState(value.type === 'custom' || value.type === 'month');

  // Generate month options for the last 24 months
  const monthOptions = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({ label: monthName, value });
    }
    return months;
  }, []);

  const presets = [
    { label: 'Last 7 Days', value: '7', type: 'days' },
    { label: 'Last 30 Days', value: '30', type: 'days' },
    { label: 'Last 90 Days', value: '90', type: 'days' },
    { label: 'Last Year', value: '365', type: 'days' },
    { label: 'Forever', value: '0', type: 'days' },
    { label: 'Select Month', value: 'month', type: 'month' },
    { label: 'Custom Range', value: 'custom', type: 'custom' }
  ];

  const comparisonPresets = [
    { label: 'Previous Period', value: 'previous' },
    { label: 'Same Period Last Year', value: 'lastyear' },
    { label: 'Previous Month', value: 'prevmonth' },
    { label: 'Custom Range', value: 'custom' }
  ];

  // Calculate comparison dates based on type
  useEffect(() => {
    if (!comparisonMode || !onComparisonChange) return;

    // For custom type, only set initial defaults if dates are empty or invalid
    if (comparisonType === 'custom') {
      // Check if we need to set sensible defaults
      const needsDefaults = !comparisonValue?.startDate || !comparisonValue?.endDate ||
        new Date(comparisonValue.startDate) > new Date() || // Start date in future
        new Date(comparisonValue.startDate) > new Date(comparisonValue.endDate); // Start after end

      if (needsDefaults) {
        // Set default: previous 30 days before today
        const defaultEnd = new Date();
        defaultEnd.setDate(defaultEnd.getDate() - 1); // Yesterday
        const defaultStart = new Date();
        defaultStart.setDate(defaultStart.getDate() - 31); // 31 days ago

        onComparisonChange({
          startDate: defaultStart.toISOString().split('T')[0],
          endDate: defaultEnd.toISOString().split('T')[0]
        });
      }
      return;
    }

    let compStart, compEnd;
    let p1Start, p1End;

    // Get period 1 dates
    if (value.type === 'custom' || value.type === 'month') {
      p1Start = new Date(value.startDate);
      p1End = new Date(value.endDate);
    } else if (value.days === '0' || value.days === 0) {
      // "Forever" - use last 30 days as the effective period for comparison
      p1End = new Date();
      p1Start = new Date();
      p1Start.setDate(p1Start.getDate() - 30);
    } else {
      p1End = new Date();
      p1Start = new Date();
      p1Start.setDate(p1Start.getDate() - parseInt(value.days || 30));
    }

    const periodLength = Math.round((p1End - p1Start) / (1000 * 60 * 60 * 24));

    if (comparisonType === 'previous') {
      // Previous period of same length
      compEnd = new Date(p1Start);
      compEnd.setDate(compEnd.getDate() - 1);
      compStart = new Date(compEnd);
      compStart.setDate(compStart.getDate() - periodLength);
    } else if (comparisonType === 'lastyear') {
      // Same period last year
      compStart = new Date(p1Start);
      compStart.setFullYear(compStart.getFullYear() - 1);
      compEnd = new Date(p1End);
      compEnd.setFullYear(compEnd.getFullYear() - 1);
    } else if (comparisonType === 'prevmonth') {
      // Previous month
      compStart = new Date(p1Start);
      compStart.setMonth(compStart.getMonth() - 1);
      compEnd = new Date(p1End);
      compEnd.setMonth(compEnd.getMonth() - 1);
    }

    if (compStart && compEnd) {
      onComparisonChange({
        startDate: compStart.toISOString().split('T')[0],
        endDate: compEnd.toISOString().split('T')[0]
      });
    }
  }, [comparisonMode, comparisonType, value, onComparisonChange, comparisonValue?.startDate, comparisonValue?.endDate]);

  useEffect(() => {
    setShowCustom(value.type === 'custom' || value.type === 'month');
  }, [value.type]);

  const handlePresetChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'custom') {
      setShowCustom(true);
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      onChange({ type: 'custom', startDate, endDate });
    } else if (selectedValue === 'month') {
      setShowCustom(true);
      // Default to current month
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      onChange({ type: 'month', startDate, endDate, month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` });
    } else {
      setShowCustom(false);
      onChange({ type: 'preset', days: selectedValue });
    }
  };

  const handleMonthChange = (e) => {
    const monthValue = e.target.value;
    const [year, month] = monthValue.split('-').map(Number);
    const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
    const endDate = new Date(year, month, 0).toISOString().split('T')[0];
    onChange({ type: 'month', startDate, endDate, month: monthValue });
  };

  const getCurrentSelectValue = () => {
    if (value.type === 'month') return 'month';
    if (value.type === 'custom') return 'custom';
    return value.days || '30';
  };

  // Format comparison period label
  const getComparisonLabel = () => {
    if (!comparisonValue?.startDate || !comparisonValue?.endDate) return '';
    const start = new Date(comparisonValue.startDate);
    const end = new Date(comparisonValue.endDate);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Primary Time Range */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600 font-medium">Time Range:</label>
          <select
            value={getCurrentSelectValue()}
            onChange={handlePresetChange}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {presets.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        {value.type === 'month' && (
          <div className="flex items-center gap-2">
            <FiCalendar className="text-gray-500" />
            <select
              value={value.month || ''}
              onChange={handleMonthChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {monthOptions.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        )}

        {value.type === 'custom' && (
          <div className="flex items-center gap-2">
            <FiCalendar className="text-gray-500" />
            <input
              type="date"
              value={value.startDate || ''}
              onChange={(e) => onChange({ ...value, type: 'custom', startDate: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={value.endDate || ''}
              onChange={(e) => onChange({ ...value, type: 'custom', endDate: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        )}

        {showComparison && onComparisonModeChange && (
          <button
            onClick={() => onComparisonModeChange(!comparisonMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              comparisonMode
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FiLayers />
            Compare
          </button>
        )}
      </div>

      {/* Comparison Controls */}
      {comparisonMode && onComparisonChange && (
        <div className="flex flex-wrap items-center gap-4 pl-4 border-l-4 border-secondary-300 bg-secondary-50 p-3 rounded-r-lg">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 font-medium">Compare to:</label>
            <select
              value={comparisonType || 'custom'}
              onChange={(e) => onComparisonTypeChange?.(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent bg-white"
            >
              {comparisonPresets.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {comparisonType === 'custom' ? (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={comparisonValue?.startDate || ''}
                onChange={(e) => onComparisonChange({ ...comparisonValue, startDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={comparisonValue?.endDate || ''}
                onChange={(e) => onComparisonChange({ ...comparisonValue, endDate: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              />
            </div>
          ) : (
            <div className="text-sm text-secondary-700 font-medium">
              {getComparisonLabel()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeRangeSelector;
